import {ApiRepository} from '../base/api-repository';
import {action, makeObservable, observable} from 'mobx';
import {UserStore} from '../user/user-store';
import {LabelStore} from '../base/label-store';
import {ViewStore} from '../base/view-store';
import {periodIdFromRange, todayAsISOString} from '../base/dateUtils';
import {
    CategoryCard,
    DashboardPageTab,
    FeedbackCard,
    Filter,
    FilterOption,
    OverviewCard,
    Period,
    QuestionCard,
    QuestionCards,
} from './team-results-store';
import {RouterStore} from 'mobx-router';
import {RootStore} from '../base/root-store';
import {routes} from '../base/routes';
import {DateRange} from 'react-day-picker';
import {Category} from '../questions/questions-store';

export type MyDashboardModal = 'help-my-feedback' | 'help-my-overview' | 'help-my-category';

export class MyResultsStore extends ViewStore {
    @observable
    payload?: MyResultsPayload = undefined;
    @observable
    variables: MyResultsVariables = new MyResultsVariables();
    @observable
    categories?: Category[] = undefined;

    constructor(
        private api: ApiRepository,
        labelStore: LabelStore,
        private userStore: UserStore,
        private router: RouterStore<RootStore>,
    ) {
        super(labelStore);
        makeObservable(this);
    }

    @action
    onEnter(): void {
        this.variables.openTab = 'my-results';
        this.variables.categoryChoice = 'ALL';
        this.load(async () => {
            await this.refreshPayload();
            const {from, to} = this.payload!.tooltipPayload.period;

            this.variables.periodId = periodIdFromRange({from: new Date(from), to: new Date(to)} as DateRange);
        });
    }

    @action
    onDashboardTabChange(openTab: DashboardPageTab): void {
        this.variables.openTab = openTab;
        switch (openTab) {
            case 'team-results': this.router.goTo(routes.dashboard);
                break;
            case 'new-hires': this.router.goTo(routes.onboarding);
                break;
        }
    }

    @action
    onCategoryChange(categoryChoice: string): void {
        this.variables.categoryChoice = categoryChoice;
        this.refresh(async () => {
            await this.refreshPayload();
        });
    }

    @action
    onPeriodChange(periodId: string): void {
        this.variables.periodId = periodId;
        this.refresh(async () => {
            await this.refreshPayload();
        });
    }

    @action
    setCustomDateSelected(selected: boolean): void {
        this.variables.isCustomDate = selected;
    }

    @action
    openOverviewHelpModal(): void {
        this.variables.openModal = 'help-my-overview';
    }

    @action
    openFeedbackHelpModal(): void {
        this.variables.openModal = 'help-my-feedback';
    }

    @action
    openCategoryHelpModal(): void {
        this.variables.openModal = 'help-my-category';
    }

    @action
    closeModal(): void {
        this.variables.openModal = undefined;
    }

    visibleQuestionCards(): QuestionCard[] {
        const visibleQuestionCards: QuestionCard[] = [];
        const questions = this.payload!.questions!.questions;

        if (questions.length <= this.variables.shownQuestionCardsAmount) {
            return questions;
        } else {
            for (let i = 0; i < this.variables.shownQuestionCardsAmount; i++){
                visibleQuestionCards.push(questions[i]);
            }

            return visibleQuestionCards;
        }
    }

    shouldShowLoadMoreButton(): boolean {
        const questions = this.payload!.questions!.questions;
        const visibleQuestions = this.visibleQuestionCards();

        return questions > visibleQuestions;
    }

    loadMoreQuestionCards(): void {
        const totalNumberOfQuestionCards = this.payload!.questions?.questions.length;

        if (totalNumberOfQuestionCards !== undefined) {
            if (totalNumberOfQuestionCards >= this.variables.shownQuestionCardsAmount + 21) {
                this.variables.shownQuestionCardsAmount = this.variables.shownQuestionCardsAmount + 21;
            } else {
                this.variables.shownQuestionCardsAmount = (totalNumberOfQuestionCards - this.variables.shownQuestionCardsAmount) + this.variables.shownQuestionCardsAmount;
            }
        } else {
            return;
        }
    }

    timeframeFilterLabels(filterOption: FilterOption): string | undefined {
        if (filterOption.periodLength === 7 && filterOption.periodTo === todayAsISOString()) {
            return this.label('common.filter.period.current-week');
        } else if (filterOption.periodLength === 30 && filterOption.periodTo === todayAsISOString()) {
            return this.label('common.filter.period.past-month');
        } else {
            return undefined;
        }
    }

    async refreshPayload(): Promise <void> {
        const queryParameters: Record<string, string> = {};

        if (this.variables.categoryChoice !== undefined) {
            queryParameters.category = this.variables.categoryChoice;
        }
        if (this.variables.periodId !== undefined) {
            queryParameters['period-id'] = this.variables.periodId;
        }

        const queryParamsString = new URLSearchParams(queryParameters).toString();

        this.payload = await this.api.get<MyResultsPayload>(`/personal-dashboard/employee/${this.userStore.payload!.employeeId}?${queryParamsString}`);
        this.categories = await this.api.get<Category[]>(`/categories/employee/${this.userStore.payload!.employeeId}`);
    }
}

interface MyResultsPayload {
    filter: Filter;
    tooltipPayload: MyTooltipPayload;
    overview?: OverviewCard;
    feedback?: FeedbackCard;
    categories?: CategoryCard;
    questions?: QuestionCards;
    adHocQuestions?: QuestionCards;
}

export interface MyTooltipPayload {
    period: Period,
    categoryId: string,
    compareToCategoryId: string,
}

class MyResultsVariables {
    @observable
    openTab?: DashboardPageTab = undefined;
    @observable
    openModal?: MyDashboardModal = undefined;
    @observable
    categoryChoice?: string = undefined;
    @observable
    periodId?: string = undefined;
    @observable
    shownQuestionCardsAmount: number = 21;
    @observable
    isCustomDate: boolean = false;

    constructor() {
        makeObservable(this);
    }
}
